import React, { useRef } from "react";

import {
  IgAutocomplete,
  IgBadge,
  IgCheckbox,
  IgDivider,
  IgLink,
  IgTextField,
  IgTooltip,
  IgTypography,
  IgTypographyTooltip,
} from "@intelligems/ig-theme";
import { MenuItem, Paper, type PaperProps, Stack } from "@mui/material";

interface ProductFilterItemProps {
  name: string;
  items: {
    label: string;
    value: string;
  }[];
  onChange: (value: string[]) => void;
  selectedValues: string[];
  disabled?: boolean;
}

export const ProductFilterItem = ({
  name,
  items,
  selectedValues,
  onChange,
  disabled,
}: ProductFilterItemProps) => {
  // MUI compares PaperComponent by reference — a fresh function every render
  // would unmount/remount the popper Paper (resetting listbox scroll, etc.) on
  // every selection. We freeze the component identity via useRef and read live
  // values through liveRef so the closure stays current without changing.
  const liveRef = useRef({ selectedValues, onChange });
  liveRef.current = { selectedValues, onChange };

  const CustomPaper = useRef((props: PaperProps) => {
    const { selectedValues, onChange } = liveRef.current;
    return (
      <Paper
        {...props}
        sx={{
          ...props.sx,
          "& .MuiAutocomplete-listbox": { pt: 0 },
        }}
      >
        <Stack
          onMouseDown={(e) => e.preventDefault()}
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            px: 2,
            py: 1,
            borderBottom: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
            position: "sticky",
            top: 0,
            zIndex: 1,
          }}
        >
          <IgTypography variant="body2" sx={{ fontWeight: 500 }}>
            {selectedValues.length} selected
          </IgTypography>
          <IgLink
            component="button"
            type="button"
            onClick={(e) => {
              e.preventDefault();
              onChange([]);
            }}
            onMouseDown={(e) => e.preventDefault()}
            variant="body2"
          >
            Clear all
          </IgLink>
        </Stack>
        {props.children}
      </Paper>
    );
  }).current;

  if (!items?.length) return null;

  const selectedSet = new Set(selectedValues);
  const sortedOptions = [
    ...items.filter((o) => selectedSet.has(o.value)),
    ...items.filter((o) => !selectedSet.has(o.value)),
  ];

  return (
    <IgTooltip
      title={
        disabled
          ? `${name} filtering is not compatible with ${name === "Tags" ? "collection" : "tag"} filtering, remove your ${name === "Tags" ? "collection" : "tag"} filters`
          : ""
      }
    >
      <IgAutocomplete
        disabled={disabled}
        multiple
        slotProps={{
          listbox: {
            sx: {
              maxHeight: "200px",
              paddingBlock: 0,
            },
          },
        }}
        disableCloseOnSelect={true}
        value={selectedValues}
        options={sortedOptions}
        PaperComponent={CustomPaper}
        groupBy={(option) => (selectedSet.has(option.value) ? "SELECTED" : "")}
        onChange={(_, newValue) => {
          const flattened = newValue
            .flat()
            .map((item) => (typeof item === "string" ? item : item.value));
          onChange(flattened);
        }}
        isOptionEqualToValue={(option, value: string | string[]) => {
          return Array.isArray(value)
            ? value.includes(option.value)
            : value === option.value;
        }}
        getOptionLabel={(option) => {
          return `${option.label}`;
        }}
        renderInput={(params) => (
          <IgTextField
            {...params}
            placeholder={name}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {selectedValues.length > 0 && (
                    <IgBadge
                      badgeContent={selectedValues.length}
                      sx={(theme) => ({
                        "& .MuiBadge-badge": {
                          backgroundColor: theme.palette.grey[700],
                          color: theme.palette.grey.A400,
                        },
                      })}
                    />
                  )}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderTags={() => null}
        disablePortal={true}
        disableClearable
        renderOption={(props, option, { selected }) => {
          const { key, ...optionProps } = props;
          return (
            <MenuItem
              key={`${key}-${props.id}`}
              {...optionProps}
              sx={{ padding: "0 !important" }}
            >
              <Stack
                sx={{
                  flexDirection: "row",
                  alignItems: "center",
                }}
              >
                <IgCheckbox size={"small"} checked={selected} />
                <IgTypographyTooltip
                  noHover
                  text={option.label}
                  textMaxWidth={"128px"}
                  textSx={{
                    fontWeight: 400,
                  }}
                  variant={"body1"}
                />
              </Stack>
            </MenuItem>
          );
        }}
        renderGroup={(params) => {
          const showDivider = params.group === "" && selectedValues.length > 0;
          return (
            <li key={params.key}>
              {showDivider && <IgDivider sx={{ my: 0.5 }} />}
              <ul style={{ padding: 0 }}>{params.children}</ul>
            </li>
          );
        }}
        fullWidth={true}
      />
    </IgTooltip>
  );
};
